// Tick → in-game time projection.
//
// Spike convention:
//   365 days  = 1 in-game year
//   yearTicks = the ticks one year spans (a calendar axis, below)
//
// The year's length is a *setting* (generations design §4a:
// `Ruleset.yearMinutes`, 2–12 real minutes, default 6), fixed for a match
// and published in the snapshot's `globals.yearMinutes`. The day of the
// year is derived proportionally with integer math — `day = (tick mod
// yearTicks) × 365 / yearTicks` — so any year length gives a valid calendar
// without a fractional tick per day. At the default (3600 ticks a year,
// 10 Hz) one in-game day is ~1 real second and one year ~6 real minutes.
//
// The match's year length reaches this module through
// `setCalendarYearTicks` (called from the snapshot summariser, which every
// snapshot — local, restored save, multiplayer — passes through), exactly
// like the `boardScale` singleton: a render-wide constant with one writer.
//
// Year/day strings start at "Year 1 · Day 1" instead of zero — there's no
// such thing as Day 0 in any real calendar and zero-based indexing reads
// like a programmer leaked into the UI.

export const DAYS_PER_YEAR = 365;
/** The classic calendar (§4a): 6 real minutes — 3600 ticks — per year. */
export const DEFAULT_YEAR_TICKS = 3600;

// ── The match calendar ──────────────────────────────────────────────────

let yearTicks = DEFAULT_YEAR_TICKS;

/**
 * Set the match's year length in ticks (from `globals.yearMinutes`).
 * Fixed for a match by the ruleset; a snapshot without the axis leaves the
 * classic calendar untouched. Guarded so out-of-range or degenerate
 * payloads can never make the calendar divide by zero or fold a year into
 * a tick.
 */
export function setCalendarYearTicks(ticks: number | null | undefined): void {
  if (typeof ticks !== 'number' || !Number.isFinite(ticks)) return;
  const t = Math.round(ticks);
  if (t < 1200 || t > 7200) return; // the contract's 2–12 min, in ticks
  yearTicks = t;
}

/** The current match's year length in ticks (test and display helper). */
export function calendarYearTicks(): number {
  return yearTicks;
}

// ── Seasons ─────────────────────────────────────────────────────────────
// Four seasons of 91 days (the 365th day folds into winter). The calendar
// owns the boundaries; the render side (render/seasons.ts) owns what each
// season looks like.

export const DAYS_PER_SEASON = 91;

export type Season = 'spring' | 'summer' | 'autumn' | 'winter';

export const SEASON_NAMES: readonly Season[] = ['spring', 'summer', 'autumn', 'winter'];

/** Season for a 1-based day of year. Day 364–365 fold into winter. */
export function seasonOfDay(day: number): Season {
  const d = Math.max(1, Math.floor(day));
  const idx = Math.min(3, Math.floor((d - 1) / DAYS_PER_SEASON));
  return SEASON_NAMES[idx] ?? 'winter';
}

export interface GameTime {
  readonly year: number; // 1-based
  readonly day: number; // 1-based, 1..365
  readonly season: Season;
}

export function tickToGameTime(tick: bigint | number): GameTime {
  const t = typeof tick === 'bigint' ? Number(tick) : tick;
  const safeTick = Math.max(0, Math.floor(t));
  const year = Math.floor(safeTick / yearTicks) + 1;
  const day = Math.floor(((safeTick % yearTicks) * DAYS_PER_YEAR) / yearTicks) + 1;
  return { year, day, season: seasonOfDay(day) };
}

export function formatGameTime(tick: bigint | number): string {
  const { year, day, season } = tickToGameTime(tick);
  const seasonLabel = season.charAt(0).toUpperCase() + season.slice(1);
  return `Year ${year} · Day ${day} · ${seasonLabel}`;
}
